import React from 'react';
import { Alert, Button, Box, Typography, Link, useTheme } from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { useNavigate } from 'react-router-dom';

const SubscriptionAlert = ({ status, trialEndsAt, isActive, userRole }) => {
    const navigate = useNavigate();
    const theme = useTheme();

    // If the user is an admin, don't show any subscription alerts
    if (userRole === 'admin') {
        return null;
    }

    const handleSubscribeClick = () => {
        navigate('/');  // Redirect to landing page for new subscriptions
    };
    
    const handleCreateCheckout = async (plan = 'solo', billingPeriod = 'monthly') => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/create-checkout-session`, {
                method: 'POST',
                credentials: 'include',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    plan: plan,
                    billing_period: billingPeriod
                }),
            });
            
            if (!response.ok) {
                console.error('Failed to create checkout session');
                return;
            }
            
            const { checkout_url } = await response.json();
            window.location.href = checkout_url;
        } catch (error) {
            console.error('Error creating checkout session:', error);
        }
    };
    
    const handleManageSubscription = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_URL}/payments/customer-portal`, {
                method: 'POST',
                credentials: 'include',
                 headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (response.ok) {
                const data = await response.json();
                if (data.url) {
                    window.location.href = data.url;
                }
            } else {
                // Optionally show an error message to the user
            }
        } catch (error) {
            // Optionally show an error message to the user
        }
    };

    // Button style extracted for reuse
    const buttonStyle = {
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        borderRadius: '8px',
        boxShadow: '0 2px 10px -2px rgba(0,0,0,0.05)',
        textTransform: 'none',
        px: 1.5,
        py: 0.5,
        fontSize: '0.8rem',
        fontWeight: 'bold',
        '&:hover': {
            backgroundColor: theme.palette.primary.dark,
            boxShadow: '0 4px 15px -3px rgba(0,0,0,0.1)',
        }
    };
    
    // Common Alert styling for action alignment
    const alertSx = {
        mt: { xs: 2, lg: 4 },
        mb: 2,
        '& .MuiAlert-action': {
            alignItems: 'center',
            padding: '0 16px 0 0' // Consistent padding for action
        }
    };

    // Handle different subscription statuses
    if (status === 'trialing') {
        let isEnded = true; // Assume ended by default if date missing
        if (trialEndsAt) { // If trial end date exists, check it
            const endDate = new Date(trialEndsAt);
            const now = new Date();
            const daysRemaining = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
            if (daysRemaining > 0) {
                // Trial Active
                isEnded = false;
                return (
                    <Alert severity="info" sx={alertSx} action={
                        <Button size="small" onClick={handleSubscribeClick} sx={buttonStyle}>
                            Subscribe Now
                        </Button>
                    }>
                        You are on a free trial with {daysRemaining} day{daysRemaining !== 1 ? 's' : ''} remaining.
                    </Alert>
                );
            } // Otherwise, isEnded remains true
        }
        
        // If trial has ended (date missing or in the past)
        if (isEnded) {
            return (
                <Alert severity="warning" sx={alertSx} action={
                    <Button size="small" onClick={() => handleCreateCheckout('solo', 'monthly')} sx={buttonStyle}>
                        Continue Improving
                    </Button>
                }>
                    Your free trial has ended. Continue improving your customer experience with actionable insights.
                </Alert>
            );
        }
    } else if (status === 'active') {
        // Subscription Active - Render nothing
         return null;
    } else if (status === 'canceled') {
        // Subscription Canceled
         return (
            <Alert severity="error" sx={alertSx} action={
                 <Button size="small" onClick={() => handleCreateCheckout('solo', 'monthly')} sx={buttonStyle}>
                    Reactivate Plan
                 </Button>
            }>
                Your subscription is canceled. Re-subscribe to reactivate monitoring and analysis.
            </Alert>
        );
    }

    // Default: Show nothing or potentially a generic inactive message if needed
    // Based on previous logic, if !isActive and status isn't handled above, 
    // another alert outside this component shows the basic "Activate" message.
    // So returning null here is fine.
    return null; 
};

export default SubscriptionAlert; 